import { useEffect, useState } from 'react';
import EmptyState from '../../../../newComponents/emptystate';
import NameAvatarComponent from '../../../../newComponents/NameAvatar/NameAvatar';
import SimpleSpinner from '../../../loader';
import { TextCss } from '../../../GlobalCss/TextCss';
import { Colors } from '../../../GlobalCss/Colors';
import { getInboxMessage, formatLocalDisplayDateFull, downloadInboxAttachment, InboxMessage, EmailAttachment } from '../personalEmailApi';
import { getAttachmentIcon } from '../emailViewHelpers';
import { GetImages } from '../../../../utils/GetImages';
import { EmailBodyIframe, getEmailDisplayContent } from './EmailBodyIframe';

// Read-only message viewer for a POP3-connected personal account — deliberately no reply/forward/star/delete
// (POP3 has no server-side concept of these; see .claude/context/emails.md Rule 12's Phase 2 tracked-exception
// note). Kept as its own component rather than adding a providerType branch inside the Gmail-specific ShowEmail.tsx,
// to avoid any risk of regressing the working Gmail thread/reply/forward flow.

const ShowInboxMessage = ({ uid }: { uid: string | null }) => {
  const [message, setMessage] = useState<InboxMessage | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!uid) {
      setMessage(null);
      return;
    }
    setLoading(true);
    getInboxMessage(uid)
      .then(setMessage)
      .catch((error) => {
        console.error('Failed to load inbox message:', error);
        setMessage(null);
      })
      .finally(() => setLoading(false));
  }, [uid]);

  if (!uid) {
    return (
      <div className="composeContainer">
        <EmptyState
          bodytext="Click on a message from the left to read it"
          isButton={false}
          isBorder={false}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="composeContainer d-flex align-items-center justify-content-center">
        <SimpleSpinner />
      </div>
    );
  }

  if (!message) {
    // POP3 servers (Gmail included) stop offering a message the instant it's marked read anywhere -- this app,
    // Gmail's own website/app, or any other client. If that happened between the list fetch and this click, the
    // message is genuinely gone with no way to recover it (no local cache/persistence for this feature).
    return (
      <div className="composeContainer">
        <EmptyState
          heading="Message Unavailable"
          bodytext={'This message is no longer available from the mail server (it may have been read elsewhere). To prevent this going forward, enable "Enable POP for all mail" in your mail provider\'s POP3 settings (Gmail: Settings > See all settings > Forwarding and POP/IMAP > POP Download).'}
          isButton={false}
          isBorder={false}
        />
      </div>
    );
  }

  const nameParts = (message.fromName ?? message.fromEmail ?? '').split(' ');

  return (
    <div className="composeContainer">
      <div className="ShowEmailSection">
        <div className="gap-4 ShowEmailSectionInner">
          <div className="userIcon">
            <NameAvatarComponent
              firstName={nameParts.length > 0 ? nameParts[0] : ''}
              lastName={nameParts.length > 1 ? nameParts[1] : ''}
              heightWidth={40}
              shape="square"
              className={TextCss.heading_700_16}
              color={Colors.blue.regular}
              backgroundColor={Colors.blue.lighter}
            />
          </div>
          <div className="email_Deatils_section">
            <div className="email_details_header">
              <div className="email_sender_detials">
                {/* Two senders can share the same display name -- title shows the real address on hover so
                    the user can tell them apart (bug/email-ui-batch2 follow-up). */}
                <p className="sub_heading text-start" title={message.fromEmail}>{message.fromName ?? message.fromEmail}</p>
                <p className="sub_text grey_subtext1">{formatLocalDisplayDateFull(message.date)}</p>
              </div>
            </div>
            <p className="normal_text grey_subtext1">{message.subject}</p>
            <EmailBodyIframe body={getEmailDisplayContent(message)} />
            {(message.attachments as EmailAttachment[] | null)?.length > 0 && (
              <div className="file-upload-List-height">
                {(message.attachments as EmailAttachment[]).map((attachment, attachmentIndex) => (
                  <div
                    key={attachment.attachmentId ?? attachmentIndex}
                    className="file-upload-item clickable"
                    onClick={() => downloadInboxAttachment(attachment)}
                  >
                    <div className="d-flex flex-start w-100 gap-2 align-items-center">
                      <img
                        style={{ height: '20px', width: '20px' }}
                        src={GetImages[getAttachmentIcon(attachment.mimeType)]}
                        alt={attachment.filename ?? 'attachment'}
                      />
                      <p className="sub_heading black fw-normal">{attachment.filename}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShowInboxMessage;
